import * as React from "react";
import { useState, useEffect, useCallback, createContext, useContext } from "react";
import axiosClient from "@/services/bedrock/AxiosClient";
import { MfaChallengeDialog } from "@/components/Modals";
import { useSocialCoachSession, useImpersonation } from "@/utils";
import { toast } from "sonner";

interface MfaChallengeContextType {
  triggerMfa: () => Promise<void>;
  mfaVerified: boolean;
}

const MfaChallengeContext = createContext<MfaChallengeContextType | undefined>(undefined);

export const useMfaChallenge = () => {
  const context = useContext(MfaChallengeContext);
  if (!context) {
    throw new Error("useMfaChallenge must be used within an MfaChallengeProvider");
  }
  return context;
};

export const MfaChallengeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [resolveQueue, setResolveQueue] = useState<(() => void)[]>([]);
  const [rejectQueue, setRejectQueue] = useState<((err: any) => void)[]>([]);

  const { session, isImpersonating, refreshSession } = useSocialCoachSession();
  const { revert } = useImpersonation();
  const [steppedUpUserId, setSteppedUpUserId] = useState<number | undefined>();

  if (!session && steppedUpUserId !== undefined) {
    setSteppedUpUserId(undefined);
  } else if (session?.mfaVerified && steppedUpUserId !== session.userId) {
    setSteppedUpUserId(session.userId);
  }

  const mfaVerified = !!session && (session.mfaVerified === true || steppedUpUserId === session.userId);

  const triggerMfa = useCallback(() => {
    if (mfaVerified) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      setResolveQueue(prev => [...prev, resolve]);
      setRejectQueue(prev => [...prev, reject]);
      setIsOpen(true);
    });
  }, [mfaVerified]);

  useEffect(() => {
    const interceptor = axiosClient.interceptors.response.use(
      response => response,
      async error => {
        const status = error.response?.status;
        const errorCode = error.response?.data?.error || error.response?.data?.code;

        if (status === 403 && (errorCode === "step_up_required" || errorCode === "mfa_required")) {
          // If we are actively impersonating, we CANNOT step up under this client's context!
          // We must automatically revert the impersonation to let the primary admin re-authenticate.
          if (isImpersonating) {
            toast.error("For your security, we’ve switched you back to your main account to confirm your identity.", {
              duration: 5000,
            });
            revert().catch(console.error);
            return Promise.reject(error);
          }

          const originalRequest = error.config;

          // Avoid duplicate triggers if already open
          return new Promise((resolve, reject) => {
            setResolveQueue(prev => [
              ...prev,
              () => {
                // Re-run the original request
                resolve(axiosClient(originalRequest));
              },
            ]);
            setRejectQueue(prev => [...prev, () => reject(error)]);

            if (!isOpen) {
              setIsOpen(true);
            }
          });
        }
        return Promise.reject(error);
      }
    );

    return () => axiosClient.interceptors.response.eject(interceptor);
  }, [isOpen, isImpersonating, revert]);

  const handleSuccess = useCallback(() => {
    if (session?.userId != null) setSteppedUpUserId(session.userId);
    refreshSession(true).catch(console.error);
    resolveQueue.forEach(resolve => resolve());
    setResolveQueue([]);
    setRejectQueue([]);
    setIsOpen(false);
  }, [refreshSession, resolveQueue, session?.userId]);

  const handleCancel = useCallback(() => {
    rejectQueue.forEach(reject => reject());
    setResolveQueue([]);
    setRejectQueue([]);
    setIsOpen(false);
  }, [rejectQueue]);

  return (
    <MfaChallengeContext.Provider value={{ triggerMfa, mfaVerified }}>
      {children}
      <MfaChallengeDialog open={isOpen} onOpenChange={setIsOpen} onSuccess={handleSuccess} onCancel={handleCancel} />
    </MfaChallengeContext.Provider>
  );
};
